/**
 * Skeleton Loading Components
 *
 * Provides placeholder UI while content is loading.
 * Supports wave (shimmer), pulse, and static animations.
 */
import { memo } from 'react'
import { motion } from 'framer-motion'

type SkeletonVariant = 'text' | 'circular' | 'rectangular' | 'rounded'
type SkeletonAnimation = 'wave' | 'pulse' | 'none'

interface SkeletonProps {
  /** Visual variant */
  variant?: SkeletonVariant
  /** Animation style */
  animation?: SkeletonAnimation
  /** Width - number (px) or string (e.g., '100%') */
  width?: number | string
  /** Height - number (px) or string */
  height?: number | string
  /** Additional CSS classes */
  className?: string
}

const shimmerAnimation = {
  initial: { backgroundPosition: '-200% 0' },
  animate: {
    backgroundPosition: '200% 0',
    transition: {
      repeat: Infinity,
      duration: 1.5,
      ease: 'linear' as const,
    },
  },
}

const pulseAnimation = {
  initial: { opacity: 0.4 },
  animate: {
    opacity: [0.4, 0.7, 0.4],
    transition: {
      repeat: Infinity,
      duration: 1.5,
      ease: 'easeInOut' as const,
    },
  },
}

/**
 * Base Skeleton component for loading placeholders
 */
export const Skeleton = memo(function Skeleton({
  variant = 'text',
  animation = 'wave',
  width,
  height,
  className = '',
}: SkeletonProps) {
  const variantClasses: Record<SkeletonVariant, string> = {
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-none',
    rounded: 'rounded-2xl',
  }

  const defaultHeight: Record<SkeletonVariant, string | number> = {
    text: '1rem',
    circular: width || 40,
    rectangular: '100px',
    rounded: '100px',
  }

  const style: React.CSSProperties = {
    width: width ?? '100%',
    height: height ?? defaultHeight[variant],
  }

  // Wave animation uses gradient shimmer
  if (animation === 'wave') {
    return (
      <motion.div
        className={`${variantClasses[variant]} ${className}`}
        style={{
          ...style,
          background:
            'linear-gradient(90deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0.08) 100%)',
          backgroundSize: '200% 100%',
        }}
        variants={shimmerAnimation}
        initial="initial"
        animate="animate"
      />
    )
  }

  // Pulse animation
  if (animation === 'pulse') {
    return (
      <motion.div
        className={`bg-white/10 ${variantClasses[variant]} ${className}`}
        style={style}
        variants={pulseAnimation}
        initial="initial"
        animate="animate"
      />
    )
  }

  // Static (no animation)
  return (
    <div
      className={`bg-white/10 ${variantClasses[variant]} ${className}`}
      style={style}
    />
  )
})

/**
 * Pre-composed skeleton for text blocks
 */
export const SkeletonText = memo(function SkeletonText({
  lines = 3,
  className = '',
}: {
  lines?: number
  className?: string
}) {
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={i === lines - 1 ? '60%' : '100%'}
        />
      ))}
    </div>
  )
})

/**
 * Pre-composed skeleton for cards
 */
export const SkeletonCard = memo(function SkeletonCard({
  className = '',
}: {
  className?: string
}) {
  return (
    <div className={`bg-white/5 rounded-3xl p-6 space-y-4 ${className}`}>
      <div className="flex items-center gap-4">
        <Skeleton variant="circular" width={48} height={48} />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="text" width="60%" />
        </div>
      </div>
      <Skeleton variant="rounded" height={120} />
      <SkeletonText lines={2} />
    </div>
  )
})

/**
 * Pre-composed skeleton for wrapped slide content
 */
export const SkeletonSlide = memo(function SkeletonSlide({
  className = '',
}: {
  className?: string
}) {
  return (
    <div
      className={`min-h-screen flex flex-col items-center justify-center p-8 ${className}`}
    >
      <div className="max-w-md w-full space-y-8 text-center">
        {/* Emoji placeholder */}
        <Skeleton
          variant="circular"
          width={80}
          height={80}
          className="mx-auto"
        />

        {/* Title area */}
        <div className="space-y-3">
          <Skeleton variant="text" width="40%" height="0.875rem" className="mx-auto" />
          <Skeleton variant="text" width="70%" height="2rem" className="mx-auto" />
        </div>

        {/* Content card */}
        <div className="bg-white/5 rounded-3xl p-6 space-y-4">
          <Skeleton variant="rounded" height={100} />
          <SkeletonText lines={3} />
        </div>

        {/* Pills/tags */}
        <div className="flex justify-center gap-3">
          <Skeleton variant="rounded" width={80} height={32} />
          <Skeleton variant="rounded" width={100} height={32} />
          <Skeleton variant="rounded" width={70} height={32} />
        </div>
      </div>
    </div>
  )
})
